import { useEffect, useRef, useState } from "react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, Upload, Trash2, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import VisToggle from "@/components/technique/VisToggle";
import { Field, NSelect, TInput } from "@/components/technique/fields";

const CATS = ["Plan", "Document", "Autorisation", "Note de calcul", "Autre"];

export default function TechDocuments({ fid, readOnly }) {
  const [docs, setDocs] = useState(null);
  const [cat, setCat] = useState("Plan");
  const [titre, setTitre] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const ref = useRef(null);
  const load = () => api.get(`/technique/files/${fid}/documents`).then((r) => setDocs(r.data));
  useEffect(() => { load(); }, [fid]); // eslint-disable-line
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("categorie", cat); fd.append("titre", titre);
      await api.post(`/technique/files/${fid}/documents`, fd); toast.success("Document ajouté"); setTitre(""); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const view = async (d) => {
    try { const r = await api.get(`/technique/documents/${d.id}`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (d) => { if (!window.confirm("Supprimer ce document ?")) return; await api.delete(`/technique/documents/${d.id}`); load(); };
  const list = (docs || []).filter((d) => !filter || d.categorie === filter);
  return (
    <div className="space-y-4">
      {!readOnly && <div className="rounded-lg border bg-white p-4 grid sm:grid-cols-4 gap-3 items-end">
        <Field label="Catégorie"><NSelect value={cat} onChange={setCat} options={CATS} testid="doc-cat" /></Field>
        <Field label="Titre" className="sm:col-span-2"><TInput value={titre} onChange={setTitre} placeholder="Ex. Plan de fondation R0" testid="doc-titre" /></Field>
        <input ref={ref} type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="doc-input" />
        <Button onClick={() => ref.current?.click()} disabled={busy} data-testid="doc-upload" className="bg-brand-600 hover:bg-brand-700 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}Téléverser</Button>
      </div>}
      <div className="w-48"><NSelect value={filter} onChange={setFilter} placeholder="Toutes catégories" options={CATS} testid="doc-filter" /></div>
      <div className="rounded-lg border bg-white overflow-hidden">
        {docs === null ? <p className="px-4 py-3 text-sm text-slate-400">Chargement…</p> : list.length === 0 ? <p className="px-4 py-3 text-sm text-slate-400">Aucun plan ni document.</p> : (
          <div className="divide-y">{list.map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-4 py-2.5 text-sm flex-wrap" data-testid={`doc-row-${d.id}`}>
              <FileText className="h-4 w-4 text-brand-600" />
              <div className="flex-1 min-w-[160px]"><p className="font-medium">{d.titre}</p><p className="text-xs text-slate-400">{d.categorie} · {d.nom} · {Math.round((d.taille || 0) / 1024)} Ko · {fmtDate(d.created_at)} · {d.by}</p></div>
              <VisToggle kind="document" id={d.id} visible={d.visible_client} testid={`doc-vis-${d.id}`} />
              <Button size="sm" variant="outline" onClick={() => view(d)} data-testid={`doc-view-${d.id}`}><Eye className="h-4 w-4 mr-1" />Ouvrir</Button>
              {!readOnly && <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => del(d)} data-testid={`doc-del-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
            </div>))}</div>
        )}
      </div>
    </div>
  );
}
